import React from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, ArrowUpRightIcon, PlayIcon } from "lucide-react";
import type { Project } from "../data/projects";
import { SunMark } from "./SunMark";
import { TreatedImage } from "./TreatedImage";
import { EYEBROW, H3 } from "../utils/typography";
type ProjectCardProps = {
  project: Project;
};
export function ProjectCard({
  project
}: ProjectCardProps) {
  const isInvite = project.kind === "invite";
  const isReel = project.kind === "reel";
  const linkClass = "group flex h-full flex-col focus:outline-none";
  const content = <>
      <div className="relative overflow-hidden rounded-3xl bg-paper ring-offset-4 ring-offset-cream group-focus-visible:ring-2 group-focus-visible:ring-rust" data-id="element-359" data-mp="26mh1x3r7b3ek">
        {isInvite ? <div className="relative aspect-[4/3] w-full bg-espresso md:aspect-[3/1]" aria-hidden="true" data-id="element-360" data-mp="v6se6a64i0cp">
            <SunMark className="absolute bottom-0 left-1/2 w-[55%] -translate-x-1/2 translate-y-[45%] text-dusty transition-transform duration-300 ease-out group-hover:translate-y-[35%] md:w-[24%]" data-id="element-361" data-mp="10651ew0wp9ft" />
          </div> : isReel ? <ReelVisual project={project} data-id="element-362" data-mp="3soicmq2wsfg9" /> : project.image && <TreatedImage src={project.image} alt={`${project.title} — ${project.client}`} className="aspect-[4/3] w-full" motionMask={project.motionMask} motionStyle={project.motionStyle} interactive data-id="element-363" data-mp="aidu4u8gf4uy" />}
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2" data-id="element-364" data-mp="feb84tw8vz4k">
        <p className={`min-w-0 ${EYEBROW} text-espresso/70`} data-id="element-365" data-mp="t70vb1nifa39">{project.client}</p>
        <ul className="hidden gap-2 sm:flex" data-id="element-366" data-mp="p7ias6zlp10y">
          {project.tags.map(tag => <li key={tag} className="whitespace-nowrap rounded-full border border-espresso/15 px-3 py-1 text-xs font-medium uppercase tracking-wide text-espresso/70" data-id="element-367" data-mp="1371iwzsewcd9">
              {tag}
            </li>)}
        </ul>
      </div>

      <div className="mt-3 flex flex-1 items-start justify-between gap-6" data-id="element-368" data-mp="3vzyr88vcaa6l">
        <div data-id="element-369" data-mp="4u5odewjxxpt">
          <h3 className={`${H3} text-espresso`} data-id="element-370" data-mp="4ufed2hlk4vi">{project.title}</h3>
          <p className="mt-2 max-w-md text-espresso/70" data-id="element-371" data-mp="4s2om35lkq7w">{project.description}</p>
          {isReel && project.credit && <p className="mt-2 max-w-md text-sm text-espresso/60" data-id="element-372" data-mp="ek8qc4lnim0a">{project.credit}</p>}
        </div>

        {isInvite ? <span className="mt-1 flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-espresso px-5 text-sm font-medium uppercase text-cream transition-colors duration-150 group-hover:bg-rust" data-id="element-373" data-mp="ji0jf1y18e62">
            {project.cta}
            <ArrowRightIcon className="h-4 w-4" data-id="element-374" data-mp="shnhpevgt3uw" />
          </span> : <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-espresso text-cream transition-colors duration-150 group-hover:bg-rust" data-id="element-375" data-mp="2pdl2wvcqulmm">
            <ArrowUpRightIcon className="h-4 w-4" data-id="element-376" data-mp="2msnb5z0ry1qc" />
          </span>}
      </div>
    </>;
  return <article className="h-full" data-id="element-377" data-mp="2i03nuacabs8c">
      {isReel ? <a href={project.href} target="_blank" rel="noopener noreferrer" aria-label={`${project.title} — ${project.cta ?? "Watch"} on Instagram (opens in a new tab)`} className={linkClass} data-id="element-378" data-mp="2d1ixr3qqyoxm">
          {content}
        </a> : <Link to={project.href}
    // Scroll even if the URL already ends in #contact (a repeat click wouldn't re-trigger)
    onClick={isInvite ? () => document.getElementById("contact")?.scrollIntoView({
      behavior: "smooth"
    }) : undefined} className={linkClass} data-id="element-379" data-mp="30rd7vdjolze6">
          {content}
        </Link>}
    </article>;
}

/** Type-led stand-in for the reel: a vertical "phone" frame with a play button and the reach numbers. */
function ReelVisual({
  project
}: {
  project: Project;
}) {
  return <div className="relative flex aspect-[4/3] w-full items-center justify-center gap-6 bg-rust px-6 sm:gap-10" aria-hidden="true" data-id="element-380" data-mp="38yxva3m60cop">
      <div className="relative aspect-[9/16] h-[82%] overflow-hidden rounded-2xl bg-espresso shadow-[0_20px_40px_-16px_rgba(15,12,10,0.55)] ring-4 ring-espresso transition-transform duration-300 ease-out group-hover:-translate-y-1" data-id="element-381" data-mp="2uatbsxfafx4v">
        {project.image &&
      // Wrapper holds the image in place; the play button is layered on top
      <div className="absolute inset-0" data-id="element-382" data-mp="3rvpn51frw04d">
            <TreatedImage src={project.image} alt="" className="h-full w-full" data-id="element-383" data-mp="3dyk9ogdqbyz3" />
          </div>}
        <span className="absolute left-1/2 top-1/2 z-10 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-cream/90 text-espresso shadow-md transition-colors duration-150 group-hover:bg-mustard" data-id="element-384" data-mp="3hyuqyp6z2bt9">
          <PlayIcon className="ml-0.5 h-5 w-5" fill="currentColor" data-id="element-385" data-mp="26ls8tpp85e6z" />
        </span>

      </div>

      {project.stats && <dl className="flex flex-col gap-4 sm:gap-6" data-id="element-386" data-mp="1z2r9zhpdc8py">
          {project.stats.map(stat => <div key={stat.label} data-id="element-387" data-mp="1u47mvdz1gqw0">
              <dt className={`${EYEBROW} text-mustard`} data-id="element-388" data-mp="28zdycggwlhwq">{stat.label}</dt>
              <dd className="mt-1 text-4xl font-medium leading-none tracking-tight text-cream sm:text-5xl" data-id="element-389" data-mp="24017zlqtpspe">
                {stat.value}
              </dd>
            </div>)}
        </dl>}
    </div>;
}